// Spec 48 — chat integrations management page.
//
// Master-detail: pick a service in the left rail, manage it in the detail panel
// across Overview / Channels / Access / Activity. Surfaces the RBAC rules
// (spec 47) and the audit log. Glass/pill language throughout.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CheckCircle2,
	ExternalLink,
	Hash,
	Loader2,
	Plus,
	RotateCcw,
	ShieldCheck,
	Trash2,
	X,
} from "lucide-react";
import { useState } from "react";
import { siDiscord } from "simple-icons";

import { GlassPanel, Pill } from "@/components/glass";
import { toast } from "@/components/toast";

// ---- shared primitives (kept local; same look as the settings page) --------

const FIELD =
	"h-9 px-3 rounded-[10px] bg-[var(--color-surface)] border border-[var(--color-border)] text-[13px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)] outline-none focus:border-[var(--color-border-hi)] transition-colors";
const FIELD_SM =
	"h-8 px-2.5 rounded-[8px] bg-[var(--color-surface)] border border-[var(--color-border)] text-[12px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)] outline-none focus:border-[var(--color-border-hi)] transition-colors";

function BrandGlyph({ icon }: { icon: { path: string; hex: string } }) {
	return (
		<svg
			viewBox="0 0 24 24"
			className="w-4 h-4 shrink-0"
			aria-hidden="true"
			fill={`#${icon.hex}`}
		>
			<path d={icon.path} />
		</svg>
	);
}

function SlackGlyph() {
	return (
		<svg
			viewBox="0 0 122.8 122.8"
			className="w-4 h-4 shrink-0"
			aria-hidden="true"
		>
			<path
				fill="#E01E5A"
				d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z"
			/>
			<path
				fill="#36C5F0"
				d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58 0 52.2 0 45.1s5.8-12.9 12.9-12.9h32.3z"
			/>
			<path
				fill="#2EB67D"
				d="M97 45.1c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9S117 58 109.9 58H97V45.1zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.2z"
			/>
			<path
				fill="#ECB22E"
				d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z"
			/>
		</svg>
	);
}

function serviceIcon(service: Service) {
	return service === "slack" ? <SlackGlyph /> : <BrandGlyph icon={siDiscord} />;
}

// ---- types + fetchers ------------------------------------------------------

type Service = "slack" | "discord";

interface Project {
	id: string;
	name: string;
}
interface ChannelBinding {
	id: string;
	channelId: string;
	channelLabel: string;
	projectId: string;
}
interface InstallView {
	id: string;
	workspaceName: string | null;
	defaultProjectId: string | null;
	channelBindings: ChannelBinding[];
}
interface AccessRule {
	id: string;
	channelId: string | null;
	subjectType: "role" | "user";
	subjectId: string;
	subjectLabel: string | null;
	effect: "allow" | "deny";
}
interface AuditEvent {
	id: string;
	service: string;
	channelId: string | null;
	userName: string | null;
	status: string;
	projectId: string | null;
	questionPreview: string | null;
	detail: string | null;
	receivedAt: number;
	completedAt: number | null;
}
interface Summary {
	last24h: { total: number; answered: number; denied: number; errors: number };
	lastEventAt: number | null;
}

async function getJSON<T>(url: string): Promise<T> {
	const r = await fetch(url, { credentials: "include" });
	if (!r.ok) throw new Error(`${url}: ${r.status}`);
	return r.json() as Promise<T>;
}

const fetchIntegrations = () =>
	getJSON<{ slack: InstallView | null; discord: InstallView | null }>(
		"/api/integrations/installs",
	);
const fetchProjects = () =>
	getJSON<{ projects: Project[] }>("/api/projects").then((b) => b.projects);
const fetchRules = (service: Service) =>
	getJSON<{ rules: AccessRule[] }>(
		`/api/integrations/access-rules?service=${service}`,
	).then((b) => b.rules);
const fetchEvents = (service: Service, status: string) =>
	getJSON<{ events: AuditEvent[] }>(
		`/api/integrations/events?service=${service}&limit=100${status ? `&status=${status}` : ""}`,
	).then((b) => b.events);
const fetchSummary = (service: Service) =>
	getJSON<Summary>(`/api/integrations/events/summary?service=${service}`);

// ---- page ------------------------------------------------------------------

const SERVICES: { id: Service; label: string }[] = [
	{ id: "slack", label: "Slack" },
	{ id: "discord", label: "Discord" },
];
const TABS = ["overview", "channels", "access", "activity"] as const;
type Tab = (typeof TABS)[number];

export default function IntegrationsPage() {
	const [service, setService] = useState<Service>("discord");
	const [tab, setTab] = useState<Tab>("overview");
	const integrationsQ = useQuery({
		queryKey: ["integrations"],
		queryFn: fetchIntegrations,
		refetchOnWindowFocus: true,
	});
	const install =
		service === "slack"
			? (integrationsQ.data?.slack ?? null)
			: (integrationsQ.data?.discord ?? null);

	return (
		<div className="h-full overflow-y-auto pt-1 pb-8">
			<div className="max-w-[860px] mx-auto">
				<h1 className="text-[18px] font-medium tracking-tight mt-2 mb-1">
					Chat integrations
				</h1>
				<p className="text-[12px] text-[var(--color-text-muted)] mb-5">
					Let your team @-mention Renketsu in Slack or Discord and get answers
					from a project's graph, in-thread.
				</p>

				<div className="grid gap-4 md:grid-cols-[200px_1fr]">
					{/* Left rail */}
					<div className="space-y-1.5">
						{SERVICES.map((s) => {
							const connected =
								s.id === "slack"
									? !!integrationsQ.data?.slack
									: !!integrationsQ.data?.discord;
							const active = s.id === service;
							return (
								<button
									key={s.id}
									type="button"
									onClick={() => {
										setService(s.id);
										setTab("overview");
									}}
									className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] border transition-colors ${
										active
											? "border-[var(--color-border-hi)] bg-[var(--color-surface-hi)]"
											: "border-[var(--color-border)] hover:bg-white/[0.03]"
									}`}
								>
									{serviceIcon(s.id)}
									<span className="text-[13px] flex-1 text-left">
										{s.label}
									</span>
									<span
										className={`w-1.5 h-1.5 rounded-full shrink-0 ${
											connected
												? "bg-emerald-400/80"
												: "bg-[var(--color-text-dim)]"
										}`}
									/>
								</button>
							);
						})}
					</div>

					{/* Detail */}
					<GlassPanel className="p-5 min-h-[320px]">
						{integrationsQ.isLoading ? (
							<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
								<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
							</div>
						) : install ? (
							<Detail
								service={service}
								install={install}
								tab={tab}
								setTab={setTab}
							/>
						) : (
							<NotConnected service={service} />
						)}
					</GlassPanel>
				</div>
			</div>
		</div>
	);
}

function connectUrl(service: Service) {
	return `/api/integrations/${service}/install`;
}

function NotConnected({ service }: { service: Service }) {
	const qc = useQueryClient();
	const connect = () => {
		window.open(connectUrl(service), "_blank", "noopener,noreferrer");
		const start = Date.now();
		const t = setInterval(() => {
			qc.invalidateQueries({ queryKey: ["integrations"] });
			if (Date.now() - start > 60_000) clearInterval(t);
		}, 3_000);
	};
	return (
		<div className="flex flex-col items-center justify-center text-center py-10 gap-3">
			<div className="w-10 h-10 rounded-[10px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center">
				{serviceIcon(service)}
			</div>
			<div className="text-[13px] text-[var(--color-text-muted)] max-w-[320px]">
				{service === "slack" ? "Slack" : "Discord"} isn't connected. Connect a
				workspace to let your team ask Renketsu from chat.
			</div>
			<Pill onClick={connect}>
				<ExternalLink className="w-3.5 h-3.5" />
				Connect {service === "slack" ? "Slack" : "Discord"}
			</Pill>
		</div>
	);
}

function Detail({
	service,
	install,
	tab,
	setTab,
}: {
	service: Service;
	install: InstallView;
	tab: Tab;
	setTab: (t: Tab) => void;
}) {
	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2.5">
				<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					{serviceIcon(service)}
				</div>
				<div>
					<div className="text-[14px] font-medium flex items-center gap-2">
						{service === "slack" ? "Slack" : "Discord"}
						<span className="text-[11px] text-emerald-400/80 inline-flex items-center gap-1">
							<CheckCircle2 className="w-3 h-3" />
							{install.workspaceName ?? "connected"}
						</span>
					</div>
				</div>
			</div>

			{/* Segmented tabs */}
			<div className="inline-flex items-center gap-1 p-1 rounded-[var(--radius-pill)] border border-[var(--color-border)] bg-[var(--color-surface)]">
				{TABS.map((t) => (
					<button
						key={t}
						type="button"
						onClick={() => setTab(t)}
						className={`px-3 py-1 rounded-[var(--radius-pill)] text-[12px] capitalize transition-colors ${
							tab === t
								? "bg-[var(--color-surface-hi)] text-[var(--color-text)] border border-[var(--color-border-hi)]"
								: "border border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
						}`}
					>
						{t}
					</button>
				))}
			</div>

			{tab === "overview" && (
				<OverviewTab service={service} install={install} />
			)}
			{tab === "channels" && (
				<ChannelsTab service={service} install={install} />
			)}
			{tab === "access" && <AccessTab service={service} install={install} />}
			{tab === "activity" && <ActivityTab service={service} />}
		</div>
	);
}

function useInvalidate() {
	const qc = useQueryClient();
	return () => qc.invalidateQueries({ queryKey: ["integrations"] });
}

function OverviewTab({
	service,
	install,
}: {
	service: Service;
	install: InstallView;
}) {
	const qc = useQueryClient();
	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
	});
	const summaryQ = useQuery({
		queryKey: ["integration-summary", service],
		queryFn: () => fetchSummary(service),
	});
	const projects = projectsQ.data ?? [];
	const s = summaryQ.data?.last24h;

	const setDefault = useMutation({
		mutationFn: async (projectId: string | null) => {
			const r = await fetch("/api/integrations/default-project", {
				method: "PUT",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ service, projectId }),
			});
			if (!r.ok) throw new Error(`set default: ${r.status}`);
		},
		onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't set default project" }),
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/integrations/disconnect", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ service }),
			});
			if (!r.ok) throw new Error(`disconnect: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Disconnected");
			qc.invalidateQueries({ queryKey: ["integrations"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't disconnect" }),
	});

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-3">
				<div className="text-[12px] text-[var(--color-text-muted)]">
					Default project
				</div>
				<select
					value={install.defaultProjectId ?? ""}
					onChange={(e) =>
						setDefault.mutate(e.target.value === "" ? null : e.target.value)
					}
					className={`${FIELD_SM} max-w-[55%]`}
				>
					<option value="">None</option>
					{projects.map((p) => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</select>
			</div>

			<div className="grid grid-cols-4 gap-2">
				{[
					["24h asks", s?.total],
					["answered", s?.answered],
					["denied", s?.denied],
					["errors", s?.errors],
				].map(([label, val]) => (
					<div
						key={label as string}
						className="rounded-[10px] border border-[var(--color-border)] bg-white/[0.02] px-3 py-2.5"
					>
						<div className="text-[18px] font-medium tabular-nums">
							{val ?? "—"}
						</div>
						<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
							{label}
						</div>
					</div>
				))}
			</div>

			<div className="pt-1 border-t border-[var(--color-border)]">
				<button
					type="button"
					onClick={() => disconnect.mutate()}
					disabled={disconnect.isPending}
					className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 inline-flex items-center gap-1 mt-3"
				>
					<Trash2 className="w-3 h-3" />
					Disconnect this workspace
				</button>
			</div>
		</div>
	);
}

function ChannelsTab({
	service,
	install,
}: {
	service: Service;
	install: InstallView;
}) {
	const invalidate = useInvalidate();
	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
	});
	const projects = projectsQ.data ?? [];
	const [channelId, setChannelId] = useState("");
	const [projectId, setProjectId] = useState("");

	const add = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/integrations/channel-bindings", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					service,
					channelId: channelId.trim(),
					projectId,
				}),
			});
			if (!r.ok) throw new Error(`add binding: ${r.status}`);
		},
		onSuccess: () => {
			setChannelId("");
			setProjectId("");
			invalidate();
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't add binding" }),
	});
	const remove = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/integrations/channel-bindings/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`remove binding: ${r.status}`);
		},
		onSuccess: invalidate,
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't remove binding" }),
	});

	return (
		<div className="space-y-3">
			<div className="text-[12px] text-[var(--color-text-muted)]">
				Bind specific channels to specific projects. Unbound channels use the
				default project.
			</div>
			{service === "discord" && (
				<div className="text-[11px] text-[var(--color-text-dim)]">
					Public channels work automatically. For a private channel, add the bot
					to the channel first.
				</div>
			)}
			<div className="space-y-1.5">
				{install.channelBindings.map((b) => {
					const proj = projects.find((p) => p.id === b.projectId);
					return (
						<div
							key={b.id}
							className="flex items-center justify-between gap-2 text-[12px] px-2.5 py-2 rounded-[8px] bg-white/[0.02] border border-[var(--color-border)]"
						>
							<div className="flex items-center gap-1.5 min-w-0">
								<Hash className="w-3 h-3 text-[var(--color-text-dim)] shrink-0" />
								<span className="truncate">{b.channelLabel}</span>
								<span className="text-[var(--color-text-dim)]">→</span>
								<span className="truncate">{proj?.name ?? b.projectId}</span>
							</div>
							<button
								type="button"
								onClick={() => remove.mutate(b.id)}
								disabled={remove.isPending}
								className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 shrink-0"
							>
								<X className="w-3 h-3" />
							</button>
						</div>
					);
				})}
			</div>
			<div className="flex items-center gap-2">
				<input
					value={channelId}
					onChange={(e) => setChannelId(e.target.value)}
					placeholder="channel id"
					className={`flex-1 min-w-0 ${FIELD_SM}`}
				/>
				<select
					value={projectId}
					onChange={(e) => setProjectId(e.target.value)}
					className={FIELD_SM}
				>
					<option value="">project…</option>
					{projects.map((p) => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</select>
				<button
					type="button"
					onClick={() => add.mutate()}
					disabled={!channelId.trim() || !projectId || add.isPending}
					className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
				>
					<Plus className="w-3.5 h-3.5" />
					Add
				</button>
			</div>
		</div>
	);
}

function AccessTab({
	service,
	install,
}: {
	service: Service;
	install: InstallView;
}) {
	const qc = useQueryClient();
	const rulesQ = useQuery({
		queryKey: ["access-rules", service],
		queryFn: () => fetchRules(service),
	});
	const rules = rulesQ.data ?? [];
	const [effect, setEffect] = useState<"allow" | "deny">("allow");
	const [subjectType, setSubjectType] = useState<"role" | "user">(
		service === "slack" ? "user" : "role",
	);
	const [subjectId, setSubjectId] = useState("");
	const [channelId, setChannelId] = useState("");

	const invalidate = () =>
		qc.invalidateQueries({ queryKey: ["access-rules", service] });

	const add = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/integrations/access-rules", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					service,
					channelId: channelId.trim() || null,
					subjectType,
					subjectId: subjectId.trim(),
					effect,
				}),
			});
			if (!r.ok) throw new Error(`add rule: ${r.status}`);
		},
		onSuccess: () => {
			setSubjectId("");
			setChannelId("");
			invalidate();
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't add rule" }),
	});
	const remove = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/integrations/access-rules/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`remove rule: ${r.status}`);
		},
		onSuccess: invalidate,
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't remove rule" }),
	});

	const Row = (r: AccessRule) => (
		<div
			key={r.id}
			className="flex items-center justify-between gap-2 text-[12px] px-2.5 py-2 rounded-[8px] bg-white/[0.02] border border-[var(--color-border)]"
		>
			<div className="flex items-center gap-2 min-w-0">
				<span
					className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
						r.effect === "allow"
							? "text-emerald-300/90 border-emerald-400/30"
							: "text-red-300/90 border-red-400/30"
					}`}
				>
					{r.effect}
				</span>
				<span className="text-[var(--color-text-dim)]">{r.subjectType}</span>
				<span className="truncate">{r.subjectLabel ?? r.subjectId}</span>
				{r.channelId && (
					<span className="text-[var(--color-text-dim)] inline-flex items-center gap-0.5">
						<Hash className="w-3 h-3" />
						{r.channelId}
					</span>
				)}
			</div>
			<button
				type="button"
				onClick={() => remove.mutate(r.id)}
				disabled={remove.isPending}
				className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 shrink-0"
			>
				<X className="w-3 h-3" />
			</button>
		</div>
	);

	return (
		<div className="space-y-3">
			<div className="flex items-start gap-2 text-[12px] text-[var(--color-text-muted)]">
				<ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-[var(--color-text-dim)]" />
				<span>
					{rules.length === 0
						? "Anyone in the workspace can ask. Add an allow rule to restrict to specific roles or people."
						: "Deny wins over allow. With any allow rule present, only matching roles/users can ask."}
				</span>
			</div>

			{rules.length > 0 && <div className="space-y-1.5">{rules.map(Row)}</div>}

			<div className="flex flex-wrap items-center gap-2 pt-1">
				<select
					value={effect}
					onChange={(e) => setEffect(e.target.value as "allow" | "deny")}
					className={FIELD_SM}
				>
					<option value="allow">allow</option>
					<option value="deny">deny</option>
				</select>
				<select
					value={subjectType}
					onChange={(e) => setSubjectType(e.target.value as "role" | "user")}
					className={FIELD_SM}
					disabled={service === "slack"}
				>
					{service === "discord" && <option value="role">role</option>}
					<option value="user">user</option>
				</select>
				<input
					value={subjectId}
					onChange={(e) => setSubjectId(e.target.value)}
					placeholder={subjectType === "role" ? "role id" : "user id"}
					className={`flex-1 min-w-[120px] ${FIELD_SM}`}
				/>
				<input
					value={channelId}
					onChange={(e) => setChannelId(e.target.value)}
					placeholder="channel id (optional)"
					className={`${FIELD_SM} w-[150px]`}
				/>
				<button
					type="button"
					onClick={() => add.mutate()}
					disabled={!subjectId.trim() || add.isPending}
					className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
				>
					<Plus className="w-3.5 h-3.5" />
					Add
				</button>
			</div>
			{service === "slack" && (
				<div className="text-[11px] text-[var(--color-text-dim)]">
					Slack supports user rules only for now.
				</div>
			)}
		</div>
	);
}

const STATUS_COLOR: Record<string, string> = {
	answered: "text-emerald-300/90 border-emerald-400/30",
	thin_retrieval: "text-amber-300/90 border-amber-400/30",
	rate_capped: "text-amber-300/90 border-amber-400/30",
	not_entitled: "text-amber-300/90 border-amber-400/30",
	access_denied: "text-amber-300/90 border-amber-400/30",
	no_install: "text-red-300/90 border-red-400/30",
	no_project: "text-red-300/90 border-red-400/30",
	error: "text-red-300/90 border-red-400/30",
};
function statusColor(s: string) {
	return (
		STATUS_COLOR[s] ??
		"text-[var(--color-text-dim)] border-[var(--color-border)]"
	);
}

function ActivityTab({ service }: { service: Service }) {
	const qc = useQueryClient();
	const [status, setStatus] = useState("");
	const eventsQ = useQuery({
		queryKey: ["integration-events", service, status],
		queryFn: () => fetchEvents(service, status),
		refetchOnWindowFocus: true,
	});
	const events = eventsQ.data ?? [];

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between gap-2">
				<select
					value={status}
					onChange={(e) => setStatus(e.target.value)}
					className={FIELD_SM}
				>
					<option value="">all statuses</option>
					{[
						"answered",
						"access_denied",
						"not_entitled",
						"rate_capped",
						"thin_retrieval",
						"no_project",
						"error",
					].map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</select>
				<button
					type="button"
					onClick={() =>
						qc.invalidateQueries({ queryKey: ["integration-events", service] })
					}
					className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] inline-flex items-center gap-1"
				>
					<RotateCcw className="w-3 h-3" />
					Refresh
				</button>
			</div>

			{eventsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : events.length === 0 ? (
				<div className="text-[12px] text-[var(--color-text-muted)] py-6 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					No activity yet.
				</div>
			) : (
				<div className="space-y-1.5">
					{events.map((e) => (
						<div
							key={e.id}
							className="px-2.5 py-2 rounded-[8px] bg-white/[0.02] border border-[var(--color-border)]"
						>
							<div className="flex items-center gap-2 text-[12px]">
								<span
									className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${statusColor(e.status)}`}
								>
									{e.status}
								</span>
								<span className="truncate flex-1">
									{e.questionPreview ?? (
										<span className="text-[var(--color-text-dim)]">
											(no text)
										</span>
									)}
								</span>
								<span className="text-[10px] text-[var(--color-text-dim)] shrink-0 tabular-nums">
									{new Date(e.receivedAt).toLocaleTimeString()}
								</span>
							</div>
							<div className="text-[10px] text-[var(--color-text-dim)] mt-0.5 flex items-center gap-2">
								{e.userName && <span>{e.userName}</span>}
								{e.channelId && (
									<span className="inline-flex items-center gap-0.5">
										<Hash className="w-2.5 h-2.5" />
										{e.channelId}
									</span>
								)}
								{e.detail && <span className="truncate">· {e.detail}</span>}
							</div>
						</div>
					))}
				</div>
			)}
			<div className="text-[10px] text-[var(--color-text-dim)] text-center pt-1">
				Activity is retained for 30 days.
			</div>
		</div>
	);
}
